/**
 * «Шаблоны» — список и редактор чек-листов (§10.7 ТЗ `ops`).
 *
 * Шаги те же, что в диалоге бота: название и участок, пункты (тип ответа,
 * диапазон, варианты, фото, критичность), расписание (дни и окно), кому,
 * предпросмотр, публикация. Черновик живёт в базе с первого шага. Правка
 * опубликованного шаблона открывает новую версию (D76): смена работает по
 * прежней, пока новую не опубликуют.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { Empty, ErrorState, Loading } from '@shell/states';
import { Icon } from '../../icons';
import { Link, navigate } from '../../router';
import { Notice, Pill } from '../../ui';
import { shiftApi } from './api';
import type {
  ItemInput,
  ScheduleInput,
  ScheduleRow,
  TemplateDetail,
  TemplateItem,
  TemplateList,
  TemplateStatus,
} from './api';
import { tabPath } from './paths';

export function Templates({ templateId }: { templateId: number | null }) {
  const lists = useQuery<TemplateList, ApiError>({
    queryKey: ['shift', 'templates'],
    queryFn: () => shiftApi.templates(),
    refetchOnWindowFocus: true,
  });
  if (lists.isPending) return <Loading />;
  if (lists.error) return <ErrorState error={lists.error} onRetry={() => lists.refetch()} />;
  return templateId !== null ? (
    <Editor id={templateId} lists={lists.data} />
  ) : (
    <TemplateIndex lists={lists.data} />
  );
}

const STATUS_COLOURS: Record<TemplateStatus, string | null> = {
  draft: null,
  changed: 'yellow',
  published: 'green',
};
const STATUS_SHORT: Record<TemplateStatus, string> = {
  draft: 'черновик',
  changed: 'есть правка',
  published: 'опубликован',
};

function TemplateIndex({ lists }: { lists: TemplateList }) {
  const client = useQueryClient();
  const [title, setTitle] = useState('');
  const [areaId, setAreaId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useMutation<TemplateDetail, ApiError, void>({
    mutationFn: () => shiftApi.createTemplate(title.trim(), areaId ? Number(areaId) : null),
    onSuccess: (template) => {
      client.invalidateQueries({ queryKey: ['shift', 'templates'] });
      client.setQueryData(['shift', 'template', template.id], template);
      navigate(tabPath('templates', template.id));
    },
    onError: (failure) => setError(failure.detail ?? 'Не получилось создать шаблон.'),
  });

  return (
    <div className="shift-stack">
      <p className="shift-hint">
        Шаблон — это чек-лист: пункты, расписание и кому он приходит. Проходят чек-листы в
        операционном боте.
      </p>
      {lists.can_edit && (
        <form
          className="shift-card shift-create"
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            create.mutate();
          }}
        >
          <label className="shift-field shift-field--grow">
            <span>Новый шаблон</span>
            <input
              className="input"
              placeholder="Например: «Открытие кухни»"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </label>
          <label className="shift-field">
            <span>Участок</span>
            <select className="select" value={areaId} onChange={(event) => setAreaId(event.target.value)}>
              <option value="">без участка</option>
              {lists.areas.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.title}
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            className="button button--primary"
            disabled={!title.trim() || create.isPending}
          >
            <Icon name="plus" size={16} /> Создать черновик
          </button>
          {error && <Notice kind="error">{error}</Notice>}
        </form>
      )}
      {lists.templates.length === 0 ? (
        <Empty title="Шаблонов пока нет" text="Создайте первый — и он начнёт приходить смене." />
      ) : (
        <section className="shift-card">
          <ul className="shift-list">
            {lists.templates.map((template) => (
              <li key={template.id}>
                <Link to={tabPath('templates', template.id)} className="shift-row">
                  <span className="shift-row__main">
                    <span className="shift-row__title">{template.title}</span>
                    <span className="faint">
                      {[
                        template.area?.title,
                        `пунктов: ${template.items}`,
                        template.schedules.join('; ') || 'расписания нет',
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  <Pill colour={STATUS_COLOURS[template.status]}>{STATUS_SHORT[template.status]}</Pill>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

type ItemMode = null | 'new' | number;

function Editor({ id, lists }: { id: number; lists: TemplateList }) {
  const client = useQueryClient();
  const [itemMode, setItemMode] = useState<ItemMode>(null);
  const [addingSchedule, setAddingSchedule] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const key = ['shift', 'template', id] as const;

  const query = useQuery<TemplateDetail, ApiError>({
    queryKey: key,
    queryFn: () => shiftApi.template(id),
    refetchOnWindowFocus: true,
  });

  const save = useMutation<TemplateDetail, ApiError, () => Promise<TemplateDetail>>({
    mutationFn: (run) => run(),
    onSuccess: (template) => {
      client.setQueryData(key, template);
      client.invalidateQueries({ queryKey: ['shift', 'templates'] });
      setItemMode(null);
      setAddingSchedule(false);
      setResult({ ok: true, text: 'Сохранено.' });
    },
    onError: (error) => setResult({ ok: false, text: error.detail ?? 'Не получилось сохранить.' }),
  });
  const publish = useMutation<{ message: string; template: TemplateDetail }, ApiError, void>({
    mutationFn: () => shiftApi.publish(id),
    onSuccess: (answer) => {
      client.setQueryData(key, answer.template);
      client.invalidateQueries({ queryKey: ['shift', 'templates'] });
      setResult({ ok: true, text: answer.message });
    },
    onError: (error) => setResult({ ok: false, text: error.detail ?? 'Не получилось опубликовать.' }),
  });

  const back = (
    <button type="button" className="shift-back" onClick={() => navigate('/shift/templates')}>
      <Icon name="back" size={16} /> К шаблонам
    </button>
  );
  if (query.isPending) return <Loading />;
  if (query.error) {
    return (
      <div className="shift-stack">
        {back}
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      </div>
    );
  }

  const template = query.data;
  const editable = template.can_edit;
  const run = (action: () => Promise<TemplateDetail>) => {
    setResult(null);
    save.mutate(action);
  };

  return (
    <div className="shift-stack">
      {back}
      <article className="shift-card" aria-label="Шаблон">
        <header className="shift-card__head">
          <div className="shift-grow">
            <h2 className="shift-card__title">{template.title}</h2>
            <p className="faint">
              Версия {template.version} · {template.status_title}
            </p>
          </div>
          <Pill colour={STATUS_COLOURS[template.status]}>{STATUS_SHORT[template.status]}</Pill>
        </header>

        {editable && (
          <HeaderForm
            template={template}
            areas={lists.areas}
            busy={save.isPending}
            onSave={(patch) => run(() => shiftApi.patchTemplate(id, patch))}
          />
        )}
        {editable && template.status === 'published' && (
          <Notice>
            Правка пунктов заведёт версию {template.version + 1}. Смена работает по версии{' '}
            {template.published_version}, пока новую не опубликуют.
          </Notice>
        )}
        {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}
      </article>

      <section className="shift-card" aria-label="Пункты">
        <header className="shift-card__head">
          <h2 className="shift-card__title">Пункты</h2>
          {editable && itemMode === null && (
            <button type="button" className="button button--small" onClick={() => setItemMode('new')}>
              <Icon name="plus" size={14} /> Добавить пункт
            </button>
          )}
        </header>
        {template.item_list.length === 0 && itemMode !== 'new' && (
          <p className="faint">Пунктов пока нет — без них шаблон не опубликовать.</p>
        )}
        <ol className="shift-items">
          {template.item_list.map((item) =>
            itemMode === item.order_no ? (
              <li key={item.order_no} className="shift-item">
                <ItemForm
                  initial={item}
                  lists={lists}
                  busy={save.isPending}
                  onCancel={() => setItemMode(null)}
                  onSave={(input) => run(() => shiftApi.replaceItem(id, item.order_no, input))}
                />
              </li>
            ) : (
              <ItemRow
                key={item.order_no}
                item={item}
                editable={editable && itemMode === null}
                onEdit={() => setItemMode(item.order_no)}
                onRemove={() => run(() => shiftApi.removeItem(id, item.order_no))}
              />
            ),
          )}
        </ol>
        {itemMode === 'new' && (
          <ItemForm
            lists={lists}
            busy={save.isPending}
            onCancel={() => setItemMode(null)}
            onSave={(input) => run(() => shiftApi.addItem(id, input))}
          />
        )}
      </section>

      <section className="shift-card" aria-label="Расписание">
        <header className="shift-card__head">
          <h2 className="shift-card__title">Расписание и кому</h2>
          {editable && !addingSchedule && (
            <button type="button" className="button button--small" onClick={() => setAddingSchedule(true)}>
              <Icon name="plus" size={14} /> Добавить расписание
            </button>
          )}
        </header>
        {template.schedule_list.length === 0 && !addingSchedule && (
          <p className="faint">Расписания нет — чек-лист не будет создаваться.</p>
        )}
        {template.schedule_list.map((schedule) => (
          <ScheduleLine
            key={schedule.id}
            schedule={schedule}
            positions={lists.positions}
            editable={editable}
            busy={save.isPending}
            onPositions={(ids) => run(() => shiftApi.setPositions(id, schedule.id, ids))}
            onRemove={() => run(() => shiftApi.removeSchedule(id, schedule.id))}
          />
        ))}
        {addingSchedule && (
          <ScheduleForm
            positions={lists.positions}
            busy={save.isPending}
            onCancel={() => setAddingSchedule(false)}
            onSave={(input) => run(() => shiftApi.addSchedule(id, input))}
          />
        )}
      </section>

      {template.preview.length > 0 && (
        <section className="shift-card" aria-label="Предпросмотр">
          <h2 className="shift-card__title">Так его увидит сотрудник</h2>
          <div className="shift-preview">
            {template.preview.map((item) => (
              <div className="shift-preview__item" key={item.position}>
                <p className="shift-preview__heading">{item.heading}</p>
                <p className="shift-row__title">{item.title}</p>
                {item.hint && <p className="faint">{item.hint}</p>}
                {item.allowed && <p>Допустимо: {item.allowed}</p>}
                {item.critical && <p className="shift-late">Пункт критичный.</p>}
                {item.choice_options && <p className="faint">Варианты: {item.choice_options.join(' · ')}</p>}
                {item.photo_required && <p className="faint">Нужна фотография.</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {editable && (
        <section className="shift-card" aria-label="Публикация">
          {template.blockers.length > 0 ? (
            <p>До публикации не хватает: {template.blockers.join(', ')}.</p>
          ) : template.can_publish ? (
            <p>Всё готово. После публикации прохождения создаются со следующего операционного дня.</p>
          ) : (
            <p className="faint">Версия {template.version} уже опубликована — смене выдаётся она.</p>
          )}
          <div className="shift-buttons">
            <button
              type="button"
              className="button button--primary"
              disabled={!template.can_publish || publish.isPending}
              onClick={() => {
                setResult(null);
                publish.mutate();
              }}
            >
              Опубликовать
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

function HeaderForm({
  template,
  areas,
  busy,
  onSave,
}: {
  template: TemplateDetail;
  areas: TemplateList['areas'];
  busy: boolean;
  onSave: (patch: { title?: string; area_id?: number }) => void;
}) {
  const [title, setTitle] = useState(template.title);
  const [areaId, setAreaId] = useState(template.area ? String(template.area.id) : '');
  const changed = title.trim() !== template.title || areaId !== (template.area ? String(template.area.id) : '');
  return (
    <form
      className="shift-create"
      onSubmit={(event) => {
        event.preventDefault();
        onSave({ title: title.trim(), ...(areaId ? { area_id: Number(areaId) } : {}) });
      }}
    >
      <label className="shift-field shift-field--grow">
        <span>Название</span>
        <input className="input" value={title} onChange={(event) => setTitle(event.target.value)} />
      </label>
      <label className="shift-field">
        <span>Участок</span>
        <select className="select" value={areaId} onChange={(event) => setAreaId(event.target.value)}>
          {!template.area && <option value="">без участка</option>}
          {areas.map((area) => (
            <option key={area.id} value={area.id}>
              {area.title}
            </option>
          ))}
        </select>
      </label>
      <button type="submit" className="button" disabled={busy || !changed || !title.trim()}>
        Сохранить
      </button>
    </form>
  );
}

function ItemRow({
  item,
  editable,
  onEdit,
  onRemove,
}: {
  item: TemplateItem;
  editable: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const marks = [
    item.field_type_title,
    item.allowed,
    item.choice_options?.join(' / '),
    item.photo_required && item.field_type !== 'photo' ? 'нужно фото' : null,
    item.is_critical ? 'критичный' : null,
    item.assignee_position ? `проблемы — должности «${item.assignee_position}»` : null,
  ].filter(Boolean);
  return (
    <li className="shift-item">
      <div className="shift-item__head">
        <span className="shift-item__title">
          {item.order_no}. {item.title}
        </span>
        {editable && (
          <span className="shift-buttons">
            <button type="button" className="button button--small" onClick={onEdit}>
              Изменить
            </button>
            <button
              type="button"
              className="button button--small button--quiet"
              aria-label={`Удалить пункт «${item.title}»`}
              onClick={onRemove}
            >
              <Icon name="trash" size={14} />
            </button>
          </span>
        )}
      </div>
      <p className="faint">{marks.join(' · ')}</p>
      {item.hint && <p className="faint">Подсказка: {item.hint}</p>}
    </li>
  );
}

function ItemForm({
  initial,
  lists,
  busy,
  onSave,
  onCancel,
}: {
  initial?: TemplateItem;
  lists: TemplateList;
  busy: boolean;
  onSave: (input: ItemInput) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [fieldType, setFieldType] = useState(initial?.field_type ?? 'bool');
  const [hint, setHint] = useState(initial?.hint ?? '');
  const [numMin, setNumMin] = useState(initial?.num_min ?? '');
  const [numMax, setNumMax] = useState(initial?.num_max ?? '');
  const [numUnit, setNumUnit] = useState(initial?.num_unit ?? '');
  const [options, setOptions] = useState((initial?.choice_options ?? []).join('\n'));
  const [photo, setPhoto] = useState(initial?.photo_required ?? false);
  const [critical, setCritical] = useState(initial?.is_critical ?? false);
  const [position, setPosition] = useState(
    initial?.assignee_position_id ? String(initial.assignee_position_id) : '',
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSave({
      title: title.trim(),
      field_type: fieldType,
      hint: hint.trim() || null,
      photo_required: photo,
      is_critical: critical,
      num_min: fieldType === 'number' ? numMin.trim() || null : null,
      num_max: fieldType === 'number' ? numMax.trim() || null : null,
      num_unit: fieldType === 'number' ? numUnit.trim() || null : null,
      choice_options:
        fieldType === 'choice'
          ? options
              .split(/[\n;]/)
              .map((option) => option.trim())
              .filter(Boolean)
          : null,
      assignee_position_id: position ? Number(position) : null,
    });
  };

  return (
    <form className="shift-confirm shift-item-form" onSubmit={submit} aria-label="Пункт шаблона">
      <label className="shift-field">
        <span>Что проверяет пункт</span>
        <input
          className="input"
          value={title}
          placeholder="Например: «Температура холодильной камеры №2»"
          onChange={(event) => setTitle(event.target.value)}
        />
      </label>
      <div className="shift-create">
        <label className="shift-field">
          <span>Ответ</span>
          <select className="select" value={fieldType} onChange={(event) => setFieldType(event.target.value)}>
            {lists.field_types.map((type) => (
              <option key={type.code} value={type.code}>
                {type.title}
              </option>
            ))}
          </select>
        </label>
        {fieldType === 'number' && (
          <>
            <label className="shift-field shift-field--short">
              <span>от</span>
              <input className="input" inputMode="decimal" value={numMin} onChange={(e) => setNumMin(e.target.value)} />
            </label>
            <label className="shift-field shift-field--short">
              <span>до</span>
              <input className="input" inputMode="decimal" value={numMax} onChange={(e) => setNumMax(e.target.value)} />
            </label>
            <label className="shift-field shift-field--short">
              <span>единица</span>
              <input className="input" value={numUnit} placeholder="°C" onChange={(e) => setNumUnit(e.target.value)} />
            </label>
          </>
        )}
      </div>
      {fieldType === 'choice' && (
        <label className="shift-field">
          <span>Варианты — каждый с новой строки</span>
          <textarea
            className="input shift-textarea"
            rows={3}
            value={options}
            onChange={(event) => setOptions(event.target.value)}
          />
        </label>
      )}
      <label className="shift-field">
        <span>Подсказка сотруднику (необязательно)</span>
        <input className="input" value={hint} onChange={(event) => setHint(event.target.value)} />
      </label>
      <div className="shift-checks">
        <label className="shift-check">
          <input type="checkbox" checked={photo || fieldType === 'photo'} disabled={fieldType === 'photo'} onChange={(e) => setPhoto(e.target.checked)} />
          Нужна фотография
        </label>
        <label className="shift-check">
          <input type="checkbox" checked={critical} onChange={(e) => setCritical(e.target.checked)} />
          Критичный пункт
        </label>
      </div>
      <p className="faint">Требовать фото на каждом пункте не стоит — чек-лист перестанут заполнять.</p>
      <label className="shift-field">
        <span>Кому уходит проблема по пункту</span>
        <select className="select" value={position} onChange={(event) => setPosition(event.target.value)}>
          <option value="">управляющему</option>
          {lists.positions.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </label>
      <div className="shift-buttons">
        <button type="button" className="button" onClick={onCancel}>
          Отмена
        </button>
        <button type="submit" className="button button--primary" disabled={busy || !title.trim()}>
          {initial ? 'Сохранить пункт' : 'Добавить пункт'}
        </button>
      </div>
    </form>
  );
}

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

function PositionChecks({
  positions,
  chosen,
  onToggle,
}: {
  positions: TemplateList['positions'];
  chosen: number[];
  onToggle: (id: number) => void;
}) {
  if (positions.length === 0) {
    return <p className="faint">Должностей нет — их заводит владелец в «Команде».</p>;
  }
  return (
    <div className="shift-checks">
      {positions.map((position) => (
        <label className="shift-check" key={position.id}>
          <input
            type="checkbox"
            checked={chosen.includes(position.id)}
            onChange={() => onToggle(position.id)}
          />
          {position.name}
        </label>
      ))}
    </div>
  );
}

function ScheduleLine({
  schedule,
  positions,
  editable,
  busy,
  onPositions,
  onRemove,
}: {
  schedule: ScheduleRow;
  positions: TemplateList['positions'];
  editable: boolean;
  busy: boolean;
  onPositions: (ids: number[]) => void;
  onRemove: () => void;
}) {
  const current = schedule.positions.map((position) => position.id);
  return (
    <div className="shift-schedule">
      <div className="shift-item__head">
        <span className="shift-row__title">
          {schedule.weekdays_text}, {schedule.window_text}
          {schedule.spans_midnight ? ' (через полночь)' : ''}
        </span>
        {editable && (
          <button type="button" className="button button--small button--quiet" disabled={busy} onClick={onRemove}>
            Убрать
          </button>
        )}
      </div>
      {editable ? (
        <PositionChecks
          positions={positions}
          chosen={current}
          onToggle={(id) =>
            onPositions(current.includes(id) ? current.filter((item) => item !== id) : [...current, id])
          }
        />
      ) : (
        <p className="faint">
          {schedule.positions.map((position) => position.name).join(', ') || 'адресатов нет'}
        </p>
      )}
    </div>
  );
}

function ScheduleForm({
  positions,
  busy,
  onSave,
  onCancel,
}: {
  positions: TemplateList['positions'];
  busy: boolean;
  onSave: (input: ScheduleInput) => void;
  onCancel: () => void;
}) {
  const [days, setDays] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
  const [from, setFrom] = useState('08:00');
  const [to, setTo] = useState('11:00');
  const [chosen, setChosen] = useState<number[]>([]);
  const toggle = (list: number[], value: number) =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value].sort();

  return (
    <form
      className="shift-confirm"
      aria-label="Новое расписание"
      onSubmit={(event) => {
        event.preventDefault();
        onSave({ weekdays: days, window_from: from, window_to: to, position_ids: chosen });
      }}
    >
      <div className="chips" role="group" aria-label="Дни недели">
        {WEEKDAYS.map((name, index) => (
          <button
            key={name}
            type="button"
            aria-pressed={days.includes(index)}
            className={`chip${days.includes(index) ? ' chip--active' : ''}`}
            onClick={() => setDays(toggle(days, index))}
          >
            {name}
          </button>
        ))}
      </div>
      <div className="shift-create">
        <label className="shift-field shift-field--short">
          <span>с</span>
          <input className="input" type="time" value={from} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label className="shift-field shift-field--short">
          <span>по</span>
          <input className="input" type="time" value={to} onChange={(event) => setTo(event.target.value)} />
        </label>
      </div>
      {to && from && to <= from && <p className="faint">Окно переходит через полночь.</p>}
      <p className="shift-subtitle">Кому</p>
      <PositionChecks positions={positions} chosen={chosen} onToggle={(id) => setChosen(toggle(chosen, id))} />
      <div className="shift-buttons">
        <button type="button" className="button" onClick={onCancel}>
          Отмена
        </button>
        <button type="submit" className="button button--primary" disabled={busy || days.length === 0}>
          Сохранить расписание
        </button>
      </div>
    </form>
  );
}
